import { Alert, Button, Classes, H5, Intent, Tag } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import { CardStack } from "components/common/CardStack";
import { FileChip } from "components/common/FileChip";
import { FileUpload } from "components/common/FileUpload";
import { segmentTrackViewBulkScenarioFileIssues } from "in_queue/analytics";
import { ScenarioAssumptionsCSVFields } from "in_queue/types/scenarioType";
import { useState } from "react";
import { Accept } from "react-dropzone";
import {
    MAX_BULK_SCENARIOS,
    RejectedBulkScenarioFile
} from "./bulkScenarioDrafts";
import css from "./BulkScenarioUploadZone.module.scss";

const ACCEPTED_FILE_TYPES: Accept = { "text/csv": [".csv"] };

const DROP_PROMPT = `Drop CSV files or click to browse (up to ${MAX_BULK_SCENARIOS} scenarios)`;

const column = (name: string) => (
    <span className={Classes.MONOSPACE_TEXT}>{name}</span>
);

/** Take in sensitivities CSVs and list the files that could not be added. */
export const BulkScenarioUploadZone: React.FC<{
    readonly projectId: string;
    readonly rejectedFiles: ReadonlyArray<RejectedBulkScenarioFile>;
    readonly onlyExclusionSupported: boolean;
    readonly onFilesSelected: (files: ReadonlyArray<File>) => void;
    readonly onReject: (message: string) => void;
    readonly onRemoveRejectedFile: (id: string) => void;
}> = ({
    projectId,
    rejectedFiles,
    onlyExclusionSupported,
    onFilesSelected,
    onReject,
    onRemoveRejectedFile
}) => {
    const [openFileId, setOpenFileId] = useState<string>();
    const openFile = rejectedFiles.find((file) => file.id === openFileId);

    const showRejection = (fileId: string) => {
        segmentTrackViewBulkScenarioFileIssues({
            projectId,
            issue: "rejected"
        });
        setOpenFileId(fileId);
    };

    return (
        <div className={css["BulkScenarioUploadZone"]}>
            <span className={Classes.TEXT_MUTED}>
                Each CSV becomes one scenario named after its file.
            </span>
            <Banner
                intent={Intent.PRIMARY}
                icon={IconNames.INFO_SIGN}
                title={
                    onlyExclusionSupported
                        ? "Only one template column is read on upload"
                        : "Only three template columns are read on upload"
                }
                description={
                    onlyExclusionSupported ? (
                        <>
                            {column(ScenarioAssumptionsCSVFields.project_id)}{" "}
                            lists the projects to withdraw.
                        </>
                    ) : (
                        <>
                            {column(ScenarioAssumptionsCSVFields.project_id)},{" "}
                            {column(
                                ScenarioAssumptionsCSVFields.eris_percentage_reduction
                            )}{" "}
                            and{" "}
                            {column(
                                ScenarioAssumptionsCSVFields.nris_percentage_reduction
                            )}{" "}
                            set each project's reduction.
                        </>
                    )
                }
            />
            <FileUpload
                multiple
                size="small"
                accept={ACCEPTED_FILE_TYPES}
                onFilesSelected={onFilesSelected}
                onReject={onReject}
                prompt={DROP_PROMPT}
            />
            {rejectedFiles.length > 0 && (
                <div className={css["BulkScenarioUploadZone-rejected"]}>
                    <H5 className={css["BulkScenarioUploadZone-heading"]}>
                        Files not added
                    </H5>
                    <CardStack>
                        {rejectedFiles.map((rejected) => (
                            <FileChip
                                key={rejected.id}
                                filename={rejected.fileName}
                                round={false}
                                tone="none"
                                actions={
                                    <div
                                        className={
                                            css["BulkScenarioUploadZone-tags"]
                                        }
                                    >
                                        <Tag
                                            size="large"
                                            round
                                            interactive
                                            intent={Intent.DANGER}
                                            icon={IconNames.ERROR}
                                            onClick={() =>
                                                showRejection(rejected.id)
                                            }
                                        >
                                            Not added
                                        </Tag>
                                        <Button
                                            variant="minimal"
                                            icon={IconNames.CROSS}
                                            aria-label={`Remove ${rejected.fileName}`}
                                            onClick={() =>
                                                onRemoveRejectedFile(
                                                    rejected.id
                                                )
                                            }
                                        />
                                    </div>
                                }
                            />
                        ))}
                    </CardStack>
                </div>
            )}
            {openFile !== undefined && (
                <Alert
                    isOpen
                    intent={Intent.DANGER}
                    icon={IconNames.ERROR}
                    confirmButtonText="Close"
                    canEscapeKeyCancel
                    canOutsideClickCancel
                    onClose={() => setOpenFileId(undefined)}
                >
                    <p>
                        <strong>{openFile.fileName} was not added</strong>
                    </p>
                    <p>{openFile.reason}</p>
                </Alert>
            )}
        </div>
    );
};
